import { Box, Typography, Stack, Divider } from "@mui/joy";
import { Sun, Maximize2, Shield, Hash, Plus, X, FileText } from "lucide-react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { FormInput, FormSelect, Button, IconButton } from "../../../shared";

export function flashingKitForSkylightType(type: string): string {
    switch (type) {
        case "Dome Fixed":
            return "Dome Kit";
        case "Flat Fixed":
        case "Roof Window":
            return "Step Flashing Kit";
        default:
            return "N/A";
    }
}

export function Skylights({ loading }: { loading?: boolean }) {
    const { control, setValue } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "Skylights",
    });

    return (
        <Box
        >
            <Box mb={2}>
                <Typography level="title-md">Skylights</Typography>
                <Typography level="body-sm" color="neutral">
                    Specify skylight types, sizes, flashing kits, and notes.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Typography
                    level="body-xs"
                    sx={{
                        textTransform: "uppercase",
                        fontWeight: "xl",
                        letterSpacing: "0.5px",
                    }}
                >
                    Skylight List
                </Typography>
                <Typography
                    level="body-xs"
                    color="neutral"
                    sx={{ mt: -0.5, mb: 1.5, fontSize: "12px" }}
                >
                    Add one or more skylights.
                </Typography>

                <Stack spacing={0}>
                    {fields.map((item, index) => (
                        <Box
                            key={item.id}
                            sx={{ mb: index !== fields.length - 1 ? 2.5 : 0 }}
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "repeat(2, 1fr)",
                                        md: "repeat(4, 1fr)",
                                    },
                                    gap: 2.5,
                                }}
                            >
                                <FormSelect
                                    name={`Skylights.${index}.Type`}
                                    label="Type"
                                    helperText="Skylight type"
                                    placeholder="Type"
                                    disabled={loading}
                                    startDecorator={<Sun size={16} />}
                                    options={[
                                        "None",
                                        "Dome Fixed",
                                        "Flat Fixed",
                                        "Roof Window",
                                    ]}
                                    onValueChange={(type) =>
                                        setValue(
                                            `Skylights.${index}.FlashingKit`,
                                            flashingKitForSkylightType(type ?? ""),
                                        )
                                    }
                                />

                                <FormSelect
                                    name={`Skylights.${index}.Size`}
                                    label="Size"
                                    helperText="Skylight size"
                                    placeholder="Size"
                                    disabled={loading}
                                    startDecorator={<Maximize2 size={16} />}
                                    options={[
                                        "None",
                                        "Small (<6.6 sf)",
                                        "Medium (6.6-9 sf)",
                                        "Large (9.1-10 sf)",
                                    ]}
                                />

                                <FormSelect
                                    name={`Skylights.${index}.FlashingKit`}
                                    label="Flashing Kit"
                                    helperText="Required flashing kit"
                                    placeholder="Flashing Kit"
                                    disabled={loading}
                                    startDecorator={<Shield size={16} />}
                                    options={[
                                        "Dome Kit",
                                        "Step Flashing Kit",
                                        "N/A",
                                    ]}
                                />

                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <FormInput
                                            name={`Skylights.${index}.Count`}
                                            label="Count"
                                            helperText="Quantity"
                                            type="number"
                                            placeholder="Qty"
                                            disabled={loading}
                                            startDecorator={<Hash size={16} />}
                                        />
                                    </Box>
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="danger"
                                        disabled={loading}
                                        onClick={() => remove(index)}
                                        sx={{
                                            mt: "auto",
                                            mb: "5px",
                                            height: "36px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <X size={20} />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 2.5, width: "calc(100% - 44px)" }}>
                                <FormInput
                                    name={`Skylights.${index}.Notes`}
                                    label="Notes"
                                    helperText="Additional details"
                                    placeholder="e.g. cracked flashing, south-facing"
                                    disabled={loading}
                                    startDecorator={<FileText size={16} />}
                                />
                            </Box>

                            {index !== fields.length - 1 && (
                                <Divider sx={{ mt: 2.5, opacity: 0.6 }} />
                            )}
                        </Box>
                    ))}
                </Stack>

                <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Plus size={16} />}
                    onClick={() =>
                        append({
                            Type: "Flat Fixed",
                            Size: "Medium (6.6-9 sf)",
                            FlashingKit: "Step Flashing Kit",
                            Count: 1,
                            Notes: "",
                        })
                    }
                    disabled={loading}
                    sx={{ alignSelf: "flex-start", mt: 2.5 }}
                >
                    Add Skylight
                </Button>
            </Box>
        </Box>
    );
}
